import { Component, input, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-password',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div class="relative">
        <input
          [type]="showPassword() ? 'text' : 'password'"
          [id]="id() || name()"
          [name]="name()"
          [placeholder]="placeholder()"
          [disabled]="disabled()"
          [readonly]="readonly()"
          [class]="inputClasses"
          [value]="value()"
          (input)="onInput($event)"
          (blur)="onBlur()"
          (focus)="onFocus()" />
        
        @if (toggleMask()) {
          <button
            type="button"
            (click)="togglePassword()"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'">
            @if (showPassword()) {
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            } @else {
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
          </button>
        }
      </div>
      
      @if (showStrength() && value()) {
        <div class="space-y-1">
          <div class="flex gap-1">
            @for (i of [1,2,3,4]; track i) {
              <div 
                [class]="getStrengthBarClasses(i)"
                class="h-1 flex-1 rounded"></div>
            }
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Strength: {{ getPasswordStrength() }}
          </p>
        </div>
      }
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class PasswordComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  toggleMask = input<boolean>(true);
  showStrength = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  control = input<AbstractControl | null | undefined>(undefined);
  size = input<ComponentSize>('md');

  value = signal<string>('');
  showPassword = signal(false);
  touched = signal(false);

  private onChange = (value: string) => {};
  private onTouched = () => {};

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.markAsTouched();
  }

  togglePassword(): void {
    this.showPassword.update(v => !v);
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  onFocus(): void {
    // Focus handling
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  getPasswordStrength(): string {
    const password = this.value();
    if (!password) return 'None';
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    const levels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    return levels[strength - 1] || 'None';
  }

  getStrengthBarClasses(index: number): string {
    const password = this.value();
    if (!password) return 'bg-gray-200 dark:bg-gray-700';
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    if (index <= strength) {
      if (strength <= 2) return 'bg-red-500';
      if (strength <= 3) return 'bg-yellow-500';
      return 'bg-green-500';
    }
    return 'bg-gray-200 dark:bg-gray-700';
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }

  get hasError(): boolean {
    const control = this.control();
    const errorMsg = this.errorMessage();
    if (control) {
      return (control.invalid && (control.touched || this.touched())) || !!errorMsg;
    }
    return this.touched() && !!errorMsg;
  }

  get displayError(): string {
    const errorMsg = this.errorMessage();
    if (errorMsg) return errorMsg;
    const control = this.control();
    if (control && control.errors) {
      if (control.errors['required']) {
        return `${this.label() || 'This field'} is required`;
      }
      if (control.errors['minlength']) {
        return `Minimum length is ${control.errors['minlength'].requiredLength}`;
      }
    }
    return '';
  }

  get inputClasses(): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'pr-12',
      'border',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:placeholder:text-gray-500'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs pr-8',
      sm: 'px-3 py-2 text-sm pr-10',
      md: 'px-4 py-3 text-base pr-12',
      lg: 'px-5 py-4 text-lg pr-14',
      xl: 'px-6 py-5 text-xl pr-16'
    };
    classes.push(sizeMap[this.size()]);

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }
}

